import {useEffect, useState } from 'react';
import {
  useReactTable,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  Row,
} from '@tanstack/react-table';

import { useVirtualizer } from '@tanstack/react-virtual';

import Filter from '@/entities/Filters/Filter';

const TableVirtualized = ({
  accessRef,
  data,
  columns,
  formatRowProps,
  sorting,
  onChangeFilters = () => {},
  rowStyle = {},
  hasFilters = true,
}: any) => {

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const table = useReactTable({
    state: {
      sorting,
    },
    data,
    columns,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    debugTable: true,
    manualPagination: true,
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 36,
    getScrollElement: () => accessRef.current,
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? element => element?.getBoundingClientRect().height
        : undefined,
  });

  useEffect(() => {
    onChangeFilters(columnFilters);
  }, [columnFilters]);

  const sortedRowsIndex = table.getSortedRowModel().rows.map(({ index }: any) => index);

  return (
    <table style={{ width: '100%' }}>
      <thead>
        {table.getHeaderGroups().map(headerGroup => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map(header => {
              return (
                <th
                  key={header.id}
                  colSpan={header.colSpan}
                  style= {{
                    width: header.getSize(),
                    textTransform: 'uppercase',
                  }}>
                  {header.isPlaceholder ? null : (
                    <>
                      <div
                        {...{
                          className: header.column.getCanSort()
                            ? 'column-title select-none'
                            : '',
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </div>
                      {hasFilters && (
                        <div className='column-filter'>
                          {header.column.getCanFilter() ? (
                            <Filter
                              column={header.column}
                              filterValue={header.column.getFilterValue()}
                              filter={(header.column as any)?.columnDef?.meta?.filter}
                            />
                          ) : null}
                        </div>
                      )}
                    </>
                  )}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          position: 'relative',
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow, rowIndex) => {
          const row = rows[virtualRow.index] as Row<any>;
          return (
            <tr
              data-index={virtualRow.index}
              ref={node => rowVirtualizer.measureElement(node)}
              key={row.id}
              style={{
                display: 'flex',
                position: 'absolute',
                transform: `translateY(${virtualRow.start}px)`,
                width: '100%',
                ...rowStyle,
              }}
              {...formatRowProps && formatRowProps(row, rowIndex, sortedRowsIndex)}
            >
              {row.getVisibleCells().map((cell: any) => {
                return (
                  <td
                    key={cell.id}
                    style={{
                      display: 'flex',
                      width: cell.column.getSize(),
                    }}
                  >
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default TableVirtualized;